import React from 'react';
import { trackOutboundLink, trackEmailClick, trackCTAClick } from './analytics';

function Culture() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">
                <span className="text-red-600">Culture</span>
                <span className="text-blue-900"> & Giving Back</span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">Freedom Contracting Group</p>
            </div>
            <a
              href="/"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Back to Home"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 via-gray-900 to-red-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Building Communities, Not Just Structures
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            At Freedom Contracting Group, we believe in giving back to the communities we serve. 
            Our commitment extends beyond construction—we're dedicated to supporting those who 
            sacrifice for our freedom and safety.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Our Values Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-12">
          <h2 className="text-3xl font-bold text-blue-900 mb-6">Our Values</h2>
          <p className="text-gray-700 leading-relaxed mb-8 text-lg">
            Freedom Contracting Group was founded on the principles of integrity, hard work, and 
            American values. We believe that true success is measured not just by the projects we 
            complete, but by the positive impact we make in our community.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-slate-50 rounded-xl">
              <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-2">Integrity</h3>
              <p className="text-gray-600">Doing the right thing, even when no one is watching</p>
            </div>
            
            <div className="text-center p-6 bg-slate-50 rounded-xl">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-2">Service</h3>
              <p className="text-gray-600">Serving our clients and community with dedication</p>
            </div>
            
            <div className="text-center p-6 bg-slate-50 rounded-xl">
              <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-2">Community</h3>
              <p className="text-gray-600">Building lasting relationships and supporting our neighbors</p>
            </div>
          </div>
        </div>

        {/* Tunnel to Towers Section */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-900 to-red-800 p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-10 h-10 text-blue-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"></path>
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold">Proud Supporter</h2>
                <p className="text-gray-200">Tunnel to Towers Foundation</p>
              </div>
            </div>
          </div>
          
          <div className="p-8 md:p-12">
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              Freedom Contracting Group is a proud supporter of the <strong>Tunnel to Towers Foundation</strong>, 
              an organization that honors the sacrifice of firefighter Stephen Siller, who laid down his 
              life to save others on September 11, 2001.
            </p>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              The Foundation continues Stephen's legacy by supporting America's heroes—our first responders 
              and military members—and their families. Through programs that provide mortgage-free homes 
              to Gold Star families, fallen first responder families, and catastrophically injured veterans, 
              Tunnel to Towers embodies the spirit of service that resonates deeply with our company values.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-900 rounded-r-lg p-6 mb-6">
              <p className="text-blue-900 font-medium italic text-lg">
                "We do good because it's the right thing to do—not for recognition, but because supporting 
                those who sacrifice for our freedom is part of who we are as Americans."
              </p>
              <p className="text-gray-600 mt-2">— Freedom Contracting Group</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://t2t.org" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackOutboundLink('https://t2t.org', 'Learn More T2T')}
                className="inline-flex items-center justify-center gap-2 bg-blue-900 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
                Learn More at T2T.org
              </a>
              <a 
                href="https://t2t.org/donate/" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackOutboundLink('https://t2t.org/donate/', 'Donate T2T')}
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                </svg>
                Donate to Tunnel to Towers
              </a>
            </div>
          </div>
        </div>

        {/* Growing Our Impact */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-12">
          <h2 className="text-3xl font-bold text-blue-900 mb-6">Growing Our Impact</h2>
          <p className="text-gray-700 leading-relaxed mb-6 text-lg">
            We're just getting started on our philanthropic journey. As Freedom Contracting Group 
            continues to grow, so will our commitment to giving back. We're actively exploring 
            additional ways to support our community, including:
          </p>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-blue-900 mb-1">Veteran Employment</h3>
                <p className="text-gray-600">Actively recruiting and supporting veterans transitioning to civilian careers in construction</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-blue-900 mb-1">Local Community Projects</h3>
                <p className="text-gray-600">Volunteering our skills and resources for community improvement projects</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-blue-900 mb-1">Trade Education</h3>
                <p className="text-gray-600">Supporting trade school programs and apprenticeships for the next generation</p>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Top Button */}
        <div className="text-center">
          <button
            onClick={scrollToTop}
            className="inline-flex items-center gap-2 bg-blue-900 text-white hover:bg-red-600 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
            </svg>
            Back to Top
          </button>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-900 to-red-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Want to Partner With Us?</h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            If you represent a charitable organization or community initiative and would like to 
            explore partnership opportunities, we'd love to hear from you.
          </p>
          <a 
            href="mailto:hello@freedomcg.net"
            onClick={() => {
              trackEmailClick('hello@freedomcg.net');
              trackCTAClick('partner_contact', 'culture_page');
            }}
            className="inline-flex items-center gap-2 bg-white text-blue-900 hover:bg-gray-100 font-semibold py-4 px-8 rounded-xl transition-all shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
}

export default Culture;